import { Link, useParams } from 'react-router-dom';
import { articles } from '@/mocks/data';
import Breadcrumb from '@/components/base/Breadcrumb';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = articles.find((a) => a.slug === slug);

  if (!article) {
    return (
      <div className="pt-20 min-h-screen bg-background-50 flex flex-col items-center justify-center">
        <div className="w-16 h-16 flex items-center justify-center rounded-full bg-background-100 text-foreground-400 mb-4">
          <i className="ri-file-unknow-line text-2xl"></i>
        </div>
        <h2 className="font-heading text-xl font-bold text-foreground-900 mb-2">文章未找到</h2>
        <p className="text-foreground-500 mb-6">你访问的文章不存在或已被移除</p>
        <Link
          to="/"
          className="px-6 py-2.5 rounded-full bg-accent-500 text-background-50 text-sm font-medium hover:bg-accent-600 transition-colors duration-200 whitespace-nowrap cursor-pointer"
        >
          返回首页
        </Link>
      </div>
    );
  }

  const relatedArticles = articles
    .filter((a) => a.categorySlug === article.categorySlug && a.id !== article.id)
    .slice(0, 3);

  const shareUrl = typeof window !== 'undefined' ? window.location.href : '';

  return (
    <div className="pt-20 min-h-screen bg-background-50">
      <Breadcrumb
        items={[
          { label: '首页', to: '/' },
          { label: article.categoryName, to: `/category/${article.categorySlug}` },
          { label: article.title },
        ]}
      />

      <article className="w-full px-4 md:px-6 lg:px-8 pb-16">
        <div className="max-w-3xl mx-auto">
          {/* Article header */}
          <header className="mb-8">
            <div className="flex items-center gap-3 mb-4">
              <Link
                to={`/category/${article.categorySlug}`}
                className="px-3 py-1 rounded-full bg-accent-100 text-accent-700 text-xs font-medium hover:bg-accent-200 transition-colors duration-200 cursor-pointer"
              >
                {article.categoryName}
              </Link>
              <span className="text-xs text-foreground-400">{article.publishDate}</span>
            </div>
            <h1 className="font-heading text-2xl md:text-3xl lg:text-4xl font-bold text-foreground-900 mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div className="flex items-center gap-4 text-sm text-foreground-500">
                <span>作者：{article.author}</span>
                <span>阅读时间：{article.readTime} 分钟</span>
              </div>
              <div className="flex items-center gap-2">
                <button
                  className="w-8 h-8 flex items-center justify-center rounded-full border border-background-200 text-foreground-500 hover:bg-background-100 hover:border-accent-300 hover:text-accent-600 transition-all duration-200 cursor-pointer"
                  aria-label="分享到微博"
                  title="分享到微博"
                >
                  <i className="ri-weibo-line text-sm"></i>
                </button>
                <button
                  className="w-8 h-8 flex items-center justify-center rounded-full border border-background-200 text-foreground-500 hover:bg-background-100 hover:border-accent-300 hover:text-accent-600 transition-all duration-200 cursor-pointer"
                  aria-label="复制链接"
                  title="复制链接"
                  onClick={() => {
                    navigator.clipboard.writeText(shareUrl);
                  }}
                >
                  <i className="ri-link text-sm"></i>
                </button>
              </div>
            </div>
          </header>

          {/* Cover image */}
          <div className="relative w-full h-56 md:h-80 rounded-lg overflow-hidden mb-8">
            <img
              src={article.coverImage}
              alt={article.title}
              className="w-full h-full object-cover"
            />
          </div>

          {/* Article content */}
          <div
            className="article-content"
            dangerouslySetInnerHTML={{ __html: article.content }}
          />

          {/* Tags */}
          <div className="mt-10 pt-8 border-t border-background-200/70">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-foreground-500">标签：</span>
              {article.tags.map((tag) => (
                <span
                  key={tag}
                  className="px-3 py-1.5 rounded-full bg-secondary-100 text-secondary-700 text-xs font-medium"
                >
                  {tag}
                </span>
              ))}
            </div>
          </div>

          {/* Author bio */}
          <div className="mt-8 p-5 bg-background-100 rounded-lg flex items-start gap-4">
            <div className="w-12 h-12 flex-shrink-0 rounded-full bg-accent-200 flex items-center justify-center">
              <span className="font-heading text-accent-700 font-bold text-lg">{article.author.charAt(0)}</span>
            </div>
            <div>
              <h4 className="font-heading text-sm font-semibold text-foreground-800">{article.author}</h4>
              <p className="text-xs text-foreground-500 mt-1 leading-relaxed">
                羁绊创作工坊签约作者，专注于姐妹情感关系与角色扮演创作研究。
              </p>
            </div>
          </div>
        </div>
      </article>

      {/* Related articles */}
      {relatedArticles.length > 0 && (
        <section className="w-full bg-background-100 py-12 md:py-16">
          <div className="w-full px-4 md:px-6 lg:px-8">
            <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-900 mb-6 md:mb-8">相关文章</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5 md:gap-6">
              {relatedArticles.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/article/${rel.slug}`}
                  className="group bg-background-50 rounded-lg overflow-hidden border border-background-200/70 hover:border-accent-300 transition-all duration-300 cursor-pointer"
                >
                  <div className="relative w-full h-40 overflow-hidden">
                    <img
                      src={rel.coverImage}
                      alt={rel.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h3 className="font-heading text-sm font-semibold text-foreground-900 mb-2 group-hover:text-accent-600 transition-colors duration-200 line-clamp-2">
                      {rel.title}
                    </h3>
                    <div className="flex items-center gap-3 text-xs text-foreground-400">
                      <span>{rel.author}</span>
                      <span>{rel.publishDate}</span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  );
}